import type { SbDatePicker } from '../../modules/web-components/sb-date-picker';
import type { SbPrettySelect } from '../../modules/web-components/sb-pretty-select';
import type { SbAlert } from "../../modules/web-components/sb-alert"

type StringRecord = Record<string, string>;
type Child = (Node | DocumentFragment | Node[] | string | null | undefined);

interface CustomTagNameMap extends HTMLElementTagNameMap {
  "sb-pretty-select": SbPrettySelect;
  "sb-alert": SbAlert;
  "sb-date-picker": SbDatePicker;
}

/**
 * Simple helper to create an element with attributes and children.
 * @param name Name of element
 * @param attrs Object of attributes. Attributes with null or undefined values are skipped.
 * @param children List of children, either Nodes or string to be converted to text nodes. Null and undefined are ignored.
 */
export function el<K extends keyof CustomTagNameMap>(name: K, attrs: Record<string, string | null | undefined>, ...children: Child[]): CustomTagNameMap[K] {
  const el = document.createElement(name);
  Object.keys(attrs).forEach(key => {
    if (attrs[key] !== null && attrs[key] !== undefined) {
      el.setAttribute(key, attrs[key]);
    }
  });
  children.forEach(child => {
    if (typeof child === 'undefined' || child === null) {
      // skip
    } else if (typeof child === 'string') {
      el.appendChild(document.createTextNode(child));
    } else if (Array.isArray(child)) {
      child.forEach(c => el.appendChild(c));
    } else {
      el.appendChild(child);
    }
  });
  return el as CustomTagNameMap[K];
}

const elBuilder = <K extends keyof CustomTagNameMap>(name: K) => (attrs: StringRecord = {}, ...children: Child[]) => el(name, attrs, ...children);

export const div = elBuilder('div');
export const p = elBuilder('p');
export const a = elBuilder('a');
export const ul = elBuilder('ul');
export const ol = elBuilder('ol');
export const li = elBuilder('li');
export const h1 = elBuilder('h1');
export const h2 = elBuilder('h2');
export const h3 = elBuilder('h3');
export const h4 = elBuilder('h4');
export const h5 = elBuilder('h5');
export const h6 = elBuilder('h6');
export const section = elBuilder('section');
export const label = elBuilder('label');
export const input = elBuilder('input');
export const i = elBuilder('i');
export const button = elBuilder('button');
export const form = elBuilder('form');
export const script = elBuilder('script');
export const span = elBuilder('span');
export const img = elBuilder('img');
export const select = elBuilder('select');
export const option = elBuilder('option');
export const strong = elBuilder('strong');
export const em = elBuilder('em');

// SiteBuilder custom elements
export const sbPrettySelect = elBuilder('sb-pretty-select');
export const sbAlert = elBuilder('sb-alert');
export const sbDatePicker = elBuilder('sb-date-picker');
